import { useEffect, useState } from "react";
import { Check, Copy, Smartphone } from "lucide-react";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";

interface QRCodeDisplayProps {
  url: string;
  size?: number;
  label?: string;
}

export function QRCodeDisplay({ url, size = 150, label }: QRCodeDisplayProps) {
  const { t } = useTranslation("polls");
  const [copied, setCopied] = useState(false);
  const [qrUrl, setQrUrl] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: size, margin: 4 })
      .then((dataUrl) => {
        if (!cancelled) setQrUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [url, size]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="inline-flex flex-col items-center gap-3">
      {/* Carte QR */}
      <div className="relative bg-white rounded-2xl shadow-xl ring-1 ring-black/8 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
        <div className="p-4">
          <img
            src={qrUrl}
            alt="QR Code"
            width={size}
            height={size}
            className="block"
          />
        </div>
      </div>

      {/* Label + Copier */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Smartphone className="w-3.5 h-3.5 shrink-0" />
          <span>{label ?? t("qrcode.scan_label")}</span>
        </div>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-all duration-200 ${
            copied
              ? "bg-green-500/20 text-green-400"
              : "bg-white/8 text-gray-400 hover:bg-white/12 hover:text-gray-300"
          }`}
        >
          {copied ? (
            <Check className="w-3 h-3" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
          {copied ? t("qrcode.copied") : t("qrcode.copy")}
        </button>
      </div>
    </div>
  );
}
